/**
 * QuotaWarningDialog — agency only.
 * Shows the balance warning before a save that would exceed the balance.
 * Never opens on a self-serve tenant (00b DQ11 refuses instead).
 *
 * Props: open, warnings (C17.3 SaveWarnings[]), onConfirm, onCancel.
 * « Confirmer » performs the save; « Annuler » keeps the review.
 *
 * @see B-popup-frontend.md step 7, UI-SPEC §2.25
 */
import { Box, Button, Dialog } from '@mui/material';
import type { SaveWarnings } from '@traaker/shared';

export interface QuotaWarningDialogProps {
  open: boolean;
  warnings: SaveWarnings[] | null;
  onConfirm: () => void;
  onCancel: () => void;
}

/** JJ/MM from an ISO date string. */
function fmtAppliesFrom(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}/${mm}`;
}

export function QuotaWarningDialog({
  open,
  warnings,
  onConfirm,
  onCancel,
}: QuotaWarningDialogProps) {
  // Don't render for empty / null — the save is direct in that case.
  if (!warnings || warnings.length === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth={false}
      PaperProps={{
        sx: {
          width: 440,
          borderRadius: 1.5,
          p: 3,
          bgcolor: 'background.paper',
          boxShadow: '0 1px 2px rgba(0,0,0,.03), 0 24px 70px rgba(0,0,0,.22)',
        },
      }}
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(11,11,15,.3)' } } }}
    >
      {/* Title */}
      <Box sx={{ fontSize: 15, fontWeight: 700, mb: 1.5 }}>
        {/* i18n: promptStudio.quota.title */}
        Au delà de votre solde
      </Box>

      {/* One block per SaveWarnings entry */}
      {warnings.map((w, i) => (
        <Box key={i} sx={{ mb: 1.5 }}>
          {typeof w.agencyBalanceDays === 'number' && (
            <Box sx={{ fontSize: 13, color: 'text.secondary', mb: 0.5 }}>
              {/* i18n: promptStudio.quota.balanceDays */}
              Votre solde couvre environ {w.agencyBalanceDays} jour{w.agencyBalanceDays !== 1 ? 's' : ''}
            </Box>
          )}
          {w.appliesFrom && (
            <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
              {/* i18n: promptStudio.quota.appliesFrom */}
              Appliqué à partir du prochain lancement le {fmtAppliesFrom(w.appliesFrom)}
            </Box>
          )}
        </Box>
      ))}

      {/* Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
        <Button variant="text" size="small" onClick={onCancel}>
          {/* i18n: common.cancel */}
          Annuler
        </Button>
        <Button variant="contained" size="small" onClick={onConfirm} sx={{ fontWeight: 600 }}>
          {/* i18n: common.confirm */}
          Confirmer
        </Button>
      </Box>
    </Dialog>
  );
}
